// frontend/src/pages/player/EventRegistration.js
import React, { useEffect, useState, useContext } from "react";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import { resolveAssetUrl } from "../../api/config";
import { errorAlert, successAlert, warningAlert } from "../../utils/alert";
import Swal from "sweetalert2";
import { FaSpinner, FaExclamationTriangle, FaAddressBook } from "react-icons/fa";
import DistrictContactsModal from "../../components/common/DistrictContactsModal";

const isRelayGame = (game) => {
  const name = String(game?.game_name || "").toLowerCase();
  const narration = String(game?.game_narration || "").toLowerCase();
  return /(relay|4x)/.test(`${name} ${narration}`);
};

const formatEventDate = (raw) => {
  if (!raw) return "Date TBD";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString();
};

const formatEventDateRange = (startRaw, endRaw) => {
  const start = startRaw || null;
  const end = endRaw || startRaw || null;
  if (!start && !end) return "Date TBD";
  if (!start) return formatEventDate(end);
  if (!end || end === start) return formatEventDate(start);
  return `${formatEventDate(start)} to ${formatEventDate(end)}`;
};

const normalizeClassificationToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const requiresGuideRunnerForClassification = (classification) => {
  const token = normalizeClassificationToken(classification);
  if (!token) return false;
  if (/^(B|B1|B2|B3)$/.test(token)) return true;
  if (/^(T1[123]|F1[123]|S1[123]|SB1[123]|SM1[123]|PTVI|VI|ARVI)$/.test(token)) return true;
  if (token.includes("BLIND") || token.includes("VISUAL")) return true;
  return false;
};

const isClassificationAllotted = (classification) => {
  const token = normalizeClassificationToken(classification);
  if (!token) return false;
  if (token.includes("PENDING") || token.includes("NOTALLOT")) return false;
  return ![
    "NOTCLASSIFIED",
    "NOTALLOTTED",
    "CLASSNOTALLOTTED",
    "CLASSIFICATIONNOTALLOTTED",
    "UNCLASSIFIED",
    "PENDING",
    "CLASSPENDING",
    "CLASSIFICATIONPENDING",
    "CLASSALLOTMENTPENDING",
    "CLASSIFICATIONALLOTMENTPENDING",
    "NA",
  ].includes(token);
};

const isGuideRunnerType = (type) => {
  const token = String(type || "").trim().toLowerCase();
  return token.includes("guide");
};

const formatAgeCategoryLabel = (value) => {
  const token = String(value || "").toLowerCase();
  if (token === "sub_junior") return "Sub-Junior";
  if (token === "junior") return "Junior";
  if (token === "senior") return "Senior";
  return token ? token : "Unknown";
};

const normalizeGenderLabel = (value) => {
  const token = String(value || "").trim().toLowerCase();
  if (token === "male" || token === "men") return "male";
  if (token === "female" || token === "women") return "female";
  return "";
};

const ageCategoryFromDob = (rawDob) => {
  if (!rawDob) return "";
  const dob = new Date(rawDob);
  if (Number.isNaN(dob.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const birthdayThisYear = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
  if (today < birthdayThisYear) age -= 1;
  if (age <= 14) return "sub_junior";
  if (age <= 17) return "junior";
  return "senior";
};

const EventRegistration = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [popupData, setPopupData] = useState({});
  // escorts: support up to 2 escorts, optional
  const [escorts, setEscorts] = useState([
    { escort_id: "", escort_name: "", escort_email: "", escort_type: "", escort_photo: null },
    { escort_id: "", escort_name: "", escort_email: "", escort_type: "", escort_photo: null },
  ]);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [playerPhoto, setPlayerPhoto] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [eventsLoadError, setEventsLoadError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [appliedEvents, setAppliedEvents] = useState([]);
  const [showSecondEscort, setShowSecondEscort] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eligibilityContext, setEligibilityContext] = useState({
    gender: "",
    age_category: "",
    classification: "",
  });
  const [activeTab, setActiveTab] = useState("district");
  const [bankDetails, setBankDetails] = useState({
    account_name: "Para Sports Association of Andhra Pradesh",
    bank_name: "State Bank of India (SBI)",
    account_number: "41029384756",
    ifsc_code: "SBIN0001234",
    branch: "Vijayawada Main Branch"
  });
  const maxGamesAllowed = Number(popupData.max_games_per_player || 3);
  const guideRunnerRequired = requiresGuideRunnerForClassification(popupData.classification);

  const districtEvents = events.filter((e) => {
    const lvl = String(e.event_level || "district").toLowerCase().trim();
    return lvl === "district";
  });

  const stateEvents = events.filter((e) => {
    const lvl = String(e.event_level || "district").toLowerCase().trim();
    return lvl !== "district";
  });

  const activeEvents = activeTab === "district" ? districtEvents : stateEvents;

  const getEscortId = (slot) => `ESCORT${String(user?.id || 0).padStart(5, "0")}-${slot}`;

  useEffect(() => {
    if (!user || !user.id) return;

    setLoading(true);

    setEventsLoadError("");

    Promise.allSettled([
      API.fetchOpenEvents(),
      API.fetchPublicBankSettings()
    ])
      .then(([eventsResult, bankResult]) => {
        if (eventsResult.status === "rejected") {
          throw eventsResult.reason;
        }

        const eventsRes = eventsResult.value;
        // fetchOpenEvents already unwraps the API response to its data array.
        // Keep support for a raw API envelope as well so this page does not
        // silently discard valid events if the client contract changes.
        const list = Array.isArray(eventsRes)
          ? eventsRes
          : (eventsRes?.data ?? []);
        setEvents(Array.isArray(list) ? list : []);

        const bankRes = bankResult.status === "fulfilled" ? bankResult.value : null;
        if (bankRes?.account_name) {
          setBankDetails(bankRes);
        }
      })
      .catch((err) => {
        console.error("Error loading open events or bank settings:", err);
        const loadMessage = err?.message || "Unable to load open events";
        setEventsLoadError(loadMessage);
        setError(loadMessage);
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    if (!message) return;
    successAlert("Registration submitted", message);
    setMessage("");
  }, [message]);

  useEffect(() => {
    if (!error) return;
    if (String(error).toLowerCase().includes("maximum")) {
      warningAlert("Selection limit", error);
    } else {
      errorAlert("Registration issue", error);
    }
    setError("");
  }, [error]);

  useEffect(() => {
    if (!user || !user.id) return;
    API.fetchPlayerRegistrations(user.id)
      .then((data) => setAppliedEvents((data || []).map((r) => r.event_id)))
      .catch((err) => console.error("Error loading player registrations:", err));
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadPhoto = async () => {
      try {
        const res = await API.fetchPlayerProfile();
        const profile = res?.data ?? res ?? {};
        setPlayerProfile(profile);

        if (profile?.photo) {
          setPlayerPhoto(
            resolveAssetUrl(profile.photo, {
              folder: "players",
              entityId: user?.id,
            })
          );
        }
      } catch (err) {
        console.warn("No player photo found");
      }
    };

    loadPhoto();
  }, [user]);

  const handleSelectEvent = async (event) => {
    setSelectedEvent(event);
    try {
      const [gamesRes, classRes] = await Promise.all([
        API.fetchGamesBySport(event.sport_id, { player_id: user.id }),
        API.fetchPlayerClassification(user.id, event.sport_id),
      ]);
      const allGames = gamesRes?.data ?? gamesRes;
      const list = Array.isArray(allGames) ? allGames : [];
      const nonRelayGames = list.filter((g) => !isRelayGame(g));
      const effectiveFee = Number(event.registration_fee || 0);
      const baseFee = Number(
        typeof event.base_registration_fee !== "undefined"
          ? event.base_registration_fee
          : event.registration_fee || 0
      );
      const effectiveEscortFee = Number(event.escort_fee || 0);
      const baseEscortFee = Number(
        typeof event.base_escort_fee !== "undefined"
          ? event.base_escort_fee
          : event.escort_fee || 0
      );
      const classificationValue =
        (classRes?.classification || classRes?.data?.classification) || "";
      const classAllotted = isClassificationAllotted(classificationValue);
      const displayClassification = classAllotted ? classificationValue : "Not Allotted";
      const selectableGames = nonRelayGames;
      setGames(selectableGames);
      const guideRequired = classAllotted && requiresGuideRunnerForClassification(classificationValue);
      const firstEligibility = selectableGames.find((g) => g?.eligibility)?.eligibility || null;
      const profileGender = normalizeGenderLabel(playerProfile?.gender || user?.gender);
      const profileAgeCategory = ageCategoryFromDob(playerProfile?.dob || user?.dob);
      setEligibilityContext({
        gender: firstEligibility?.player_gender || profileGender,
        age_category: firstEligibility?.player_age_category || profileAgeCategory,
        classification: classAllotted
          ? (firstEligibility?.player_classification || displayClassification)
          : displayClassification,
      });
      setPopupData({
        event_id: event.id,
        event_name: event.title,
        event_date: formatEventDateRange(event.date, event.event_end_date),
        registration_fee: effectiveFee,
        base_registration_fee: baseFee,
        escort_fee: effectiveEscortFee,
        base_escort_fee: baseEscortFee,
        is_fee_exempt: !!event.is_fee_exempt,
        max_games_per_player: event.max_games_per_player || 3,
        classification: displayClassification,
        classification_allotted: classAllotted,
        guide_runner_required: guideRequired,
      });

      setEscorts((prev) => {
        const copy = [...prev];
        copy[0] = {
          ...copy[0],
          escort_id: getEscortId(1),
          escort_name: "",
          escort_email: "",
          escort_type: guideRequired ? "Guide Runner" : "",
          escort_photo: null,
        };
        copy[1] = {
          ...copy[1],
          escort_id: getEscortId(2),
          escort_name: "",
          escort_email: "",
          escort_type: "",
          escort_photo: null,
        };
        return copy;
      });
      setShowSecondEscort(false);
      setSelectedGames([]);
      setShowPopup(true);
    } catch (err) {
      console.error("Error loading event details:", err);
    }
  };

  const toggleGameSelection = (game) => {
    const gameId = Number(game?.id || 0);
    const eligibility = game?.eligibility || null;
    if (!eligibility?.eligible) {
      setError(eligibility?.reason || "You are not eligible for this game.");
      return;
    }
    setError("");
    setSelectedGames((prev) => {
      if (prev.includes(gameId)) {
        return prev.filter((id) => id !== gameId);
      }
      if (prev.length >= maxGamesAllowed) {
        setError(`You can select a maximum of ${maxGamesAllowed} games.`);
        return prev;
      }
      return [...prev, gameId];
    });
  };

  const handleChange = (e) => {
    const { name, files, value } = e.target;
    setPopupData({ ...popupData, [name]: files ? files[0] : value });
  };

  const handleEscortChange = (index, e) => {
    const { name, value, files } = e.target;
    setEscorts((prev) => {
      const copy = [...prev];
      const field = name.includes("_1") || name.includes("_2") ? name.replace(/_1|_2$/, "") : name;
      copy[index] = { ...copy[index], [field]: files ? files[0] : value };
      return copy;
    });
  };

  const selectedEscortCount = escorts.filter((e) => e.escort_name).length;
  const selectedGuideRunnerCount = escorts.filter(
    (e) => String(e.escort_name || "").trim() && isGuideRunnerType(e.escort_type || "Guide Runner")
  ).length;
  const totalPreview =
    (Number(popupData.registration_fee || 0) + Number(popupData.escort_fee || 0) * selectedEscortCount) || 0;

  const isFormValid =
    selectedGames.length > 0 &&
    (totalPreview <= 0 || popupData.payment_proof) &&
    (!guideRunnerRequired || selectedGuideRunnerCount > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (guideRunnerRequired && selectedGuideRunnerCount <= 0) {
      setError("Blind category requires at least one guide runner. Add guide runner details before submitting.");
      return;
    }

    if (totalPreview > 0) {
      const confirm = await Swal.fire({
        title: "Confirm Registration & Payment",
        html: `
          <div class="text-left space-y-2 text-sm text-slate-700">
            <p><strong>Event:</strong> ${popupData.event_name}</p>
            <p><strong>Registration Fee:</strong> ₹${popupData.registration_fee}</p>
            <p><strong>Escort(s) Fee:</strong> ₹${popupData.escort_fee} × ${selectedEscortCount} = ₹${popupData.escort_fee * selectedEscortCount}</p>
            <hr class="my-2 border-slate-200" />
            <p class="text-base font-bold text-slate-800">Total Payable Amount: ₹${totalPreview}</p>
            <p class="text-[11px] text-rose-600 font-semibold mt-2">⚠️ Please ensure your payment reference and receipt proof uploaded are correct.</p>
          </div>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, Confirm & Submit",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#ef4444"
      });
      if (!confirm.isConfirmed) return;
    } else {
      const confirm = await Swal.fire({
        title: "Confirm Registration",
        text: `Are you sure you want to register for ${popupData.event_name}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Register",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#3b82f6"
      });
      if (!confirm.isConfirmed) return;
    }

    const formData = new FormData();
    formData.append("player_id", user.id);
    formData.append("event_id", popupData.event_id);
    formData.append("game_ids", JSON.stringify(selectedGames));
    if (popupData.payment_proof) formData.append("payment_proof", popupData.payment_proof);
    if (popupData.payment_mode) formData.append("payment_mode", popupData.payment_mode);
    if (popupData.payment_reference) formData.append("payment_reference", popupData.payment_reference);

    escorts.forEach((esc, idx) => {
      const i = idx + 1;
      if (esc && esc.escort_name) {
        const normalizedEscortType = String(
          esc.escort_type || (guideRunnerRequired ? "Guide Runner" : "")
        ).trim();
        formData.append(`escort_id_${i}`, esc.escort_id || "");
        formData.append(`escort_name_${i}`, esc.escort_name);
        if (esc.escort_email) {
          formData.append(`escort_email_${i}`, esc.escort_email);
        }
        formData.append(`escort_type_${i}`, normalizedEscortType);
        if (esc.escort_photo) {
          formData.append(`escort_photo_${i}`, esc.escort_photo);
        }
      }
    });

    try {
      setSubmitting(true);
      const res = await API.registerEvent(formData);
      setAppliedEvents((prev) => [
        ...prev,
        popupData.event_id,
      ]);

      successAlert("Registered", res.message || "Registration submitted successfully!");
      setShowPopup(false);
    } catch (err) {
      console.error("FULL ERROR:", err);
      console.error("ERRORS:", err?.errors);
      setError(
        err?.message ||
        JSON.stringify(err?.errors) ||
        "Registration failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-indigo-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-indigo-600 mb-4"></div>
        <p className="text-lg font-medium">Loading available events...</p>
      </div>
    );
  }

  if (!loading && Array.isArray(events) && events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-gray-500 p-6">
        <img
          src="https://cdn-icons-png.flaticon.com/512/7486/7486769.png"
          alt="no-events"
          className="w-24 mb-4 opacity-50"
        />
        <h2 className="text-xl font-semibold mb-2 text-slate-800">
          {eventsLoadError ? "Unable to Load Events" : "No Open Events Found"}
        </h2>
        <p className="text-center text-sm max-w-md text-slate-500">
          {eventsLoadError || "There are currently no open events for registration. Please check back later."}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mt-2">
      <h1 className="text-2xl font-bold text-slate-855 mb-4">
        Event Registration
      </h1>

      {/* Mandatory District Intimation Notice */}
      <div className="bg-gradient-to-br from-amber-500/10 via-amber-50 to-orange-50 border-2 border-amber-400 p-4 sm:p-5 rounded-2xl mb-6 shadow-xs">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-xs shrink-0 mt-0.5">
              <FaExclamationTriangle className="text-xl" />
            </div>
            <div>
              <h3 className="font-bold text-amber-950 text-sm sm:text-base">
                Mandatory District Intimation Before Registration
              </h3>
              <p className="text-xs sm:text-sm text-amber-900 mt-1 leading-relaxed">
                Before registering for any championship or event, you <strong>must contact your corresponding District Secretary or President</strong> for prior intimation. District authorization is strictly mandatory as district officials must approve your entries; otherwise, your event registration will not be accepted.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowDistrictModal(true)}
            className="shrink-0 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition cursor-pointer"
          >
            <FaAddressBook /> View District Contacts
          </button>
        </div>
      </div>

      {/* Premium Tabbed Navigation */}
      <div className="flex border-b border-slate-200 mb-6 gap-2">
        <button
          onClick={() => setActiveTab("district")}
          className={`px-5 py-3 font-semibold text-sm transition-all relative flex items-center ${
            activeTab === "district"
              ? "text-indigo-600 font-bold"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          📍 District Level Events
          <span className="ml-2 px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-700 font-semibold">
            {districtEvents.length}
          </span>
          {activeTab === "district" && (
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-indigo-600 rounded-t" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("state")}
          className={`px-5 py-3 font-semibold text-sm transition-all relative flex items-center ${
            activeTab === "state"
              ? "text-indigo-600 font-bold"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          🏆 State & Higher Level Events
          <span className="ml-2 px-2 py-0.5 text-[11px] rounded-full bg-slate-100 text-slate-700 font-semibold">
            {stateEvents.length}
          </span>
          {activeTab === "state" && (
            <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-indigo-600 rounded-t" />
          )}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(activeEvents) && activeEvents.length > 0 ? (
          activeEvents.map((event) => (
            <div
              key={event.id}
              className={`p-5 border rounded-xl shadow-sm transition flex flex-col justify-between ${
                selectedEvent?.id === event.id ? "border-indigo-600 bg-indigo-50/40" : "border-gray-200 bg-white"
              }`}
            >
              <div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">{event.title}</h3>
                <div className="space-y-1 text-xs text-slate-500 mb-4">
                  <p>Date: {formatEventDateRange(event.date, event.event_end_date)}</p>
                  <p>Fee: ₹{event.registration_fee}</p>
                  {event.is_fee_exempt && (
                    <p className="font-semibold text-emerald-700">
                      Exemption: Registration and escort fee exempt
                    </p>
                  )}
                  {typeof event.escort_fee !== "undefined" && (
                    <p>Escort fee (per escort): ₹{event.escort_fee}</p>
                  )}
                  <p>Sport: {event.sport_name}</p>
                </div>
              </div>
              <button
                onClick={() => handleSelectEvent(event)}
                disabled={appliedEvents.includes(event.id)}
                className={`w-full py-2 rounded-lg font-semibold text-sm transition-all shadow-sm ${
                  appliedEvents.includes(event.id)
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed shadow-none"
                    : "bg-indigo-900 hover:bg-indigo-950 text-white"
                }`}
              >
                {appliedEvents.includes(event.id)
                  ? "Already Registered"
                  : "Register Now"}
              </button>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 col-span-full py-8">
            No open {activeTab === "district" ? "District" : "State & Higher"} level events available for registration at this time.
          </p>
        )}
      </div>

      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-xl p-6 w-[90%] md:w-[600px] relative shadow-lg overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-3 right-3 text-gray-500 hover:text-red-600 font-bold"
              onClick={() => setShowPopup(false)}
            >
              ✕
            </button>

            <h2 className="text-xl font-semibold text-center mb-4 text-indigo-900">
              Register for {popupData.event_name}
            </h2>

            {playerPhoto && (
              <div className="flex justify-center mb-3">
                <img
                  src={playerPhoto}
                  alt="Player"
                  className="w-20 h-20 rounded-full border-2 border-indigo-600 object-cover"
                />
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2.5 shadow-2xs">
                <FaExclamationTriangle className="text-amber-600 shrink-0 mt-0.5 text-sm" />
                <div className="leading-snug">
                  <strong className="font-bold text-amber-950">Prior District Intimation Mandatory:</strong> Please ensure you have informed your District Secretary / President before applying. District authorization is required for acceptance.
                </div>
              </div>

              <input type="text" value={popupData.event_date} readOnly className="w-full border p-2 rounded bg-gray-100 text-xs font-semibold text-slate-700" />

                <div>
                  <label className="block font-medium">Select up to {maxGamesAllowed} Games</label>
                  <p className="text-xs text-gray-600 mb-1">
                    Category: {formatAgeCategoryLabel(eligibilityContext.age_category)} | Gender:{" "}
                    {eligibilityContext.gender ? eligibilityContext.gender[0].toUpperCase() + eligibilityContext.gender.slice(1) : "Unknown"} | Class:{" "}
                    {eligibilityContext.classification || popupData.classification || "Not Classified"}
                  </p>
                  <div className="border rounded p-2 max-h-48 overflow-y-auto">
                    {games.map((g) => (
                      <label
                        key={g.id}
                        className={`flex items-start space-x-2 text-sm mb-1 p-1 rounded ${
                          !g?.eligibility?.eligible ? "bg-rose-50" : "bg-white"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedGames.includes(Number(g.id))}
                          disabled={!g?.eligibility?.eligible}
                          onChange={() => toggleGameSelection(g)}
                        />
                        <span className="flex-1">
                          <span className="font-medium">{g.game_name}</span>
                          {g?.eligibility ? (
                            <span
                              className={`ml-2 text-[11px] px-2 py-0.5 rounded ${
                                g?.eligibility?.eligible
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {g?.eligibility?.eligible ? "Eligible" : "Not Eligible"}
                            </span>
                          ) : popupData.classification_allotted === false ? null : (
                            <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-sky-100 text-sky-700">
                              Rule Not Mapped
                            </span>
                          )}
                          {g?.eligibility?.eligible_classes ? (
                            <div className="text-[11px] text-gray-500 mt-0.5">
                              Eligible classes: {g.eligibility.eligible_classes}
                            </div>
                          ) : null}
                          {g?.eligibility?.eligible && g?.eligibility?.mqs ? (
                            <div className="text-[11px] font-medium text-blue-700 mt-0.5">
                              {g?.eligibility?.competition_class ? `Class: ${g.eligibility.competition_class} | ` : ""}
                              MQS: {g.eligibility.mqs}
                              {g?.eligibility?.equipment && g.eligibility.equipment !== "-"
                                ? ` | Equipment: ${g.eligibility.equipment}`
                                : ""}
                            </div>
                          ) : null}
                          {!g?.eligibility?.eligible && g?.eligibility?.reason ? (
                            <div className="text-[11px] text-rose-700 mt-0.5">{g.eligibility.reason}</div>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <input type="text" value={popupData.classification} readOnly className="w-full border p-2 rounded bg-gray-100" />
                {guideRunnerRequired && (
                  <p className="text-xs text-amber-700 mt-1">
                    Blind category detected. Add at least one <strong>Guide Runner</strong>. Heat allocation will use
                    player+guide lane pairing (8 lanes = 4 players).
                  </p>
                )}

                {totalPreview > 0 && bankDetails && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 shadow-inner space-y-2 mb-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Association Bank Account Details</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-700">
                      <p className="font-semibold">Account Name:</p>
                      <p>{bankDetails.account_name}</p>
                      
                      <p className="font-semibold">Bank Name:</p>
                      <p>{bankDetails.bank_name}</p>
                      
                      <p className="font-semibold">Account Number:</p>
                      <p className="font-mono font-semibold">{bankDetails.account_number}</p>
                      
                      <p className="font-semibold">IFSC Code:</p>
                      <p className="font-mono font-semibold">{bankDetails.ifsc_code}</p>
                      
                      <p className="font-semibold">Branch:</p>
                      <p>{bankDetails.branch}</p>
                    </div>
                    <p className="text-[10px] text-indigo-600 font-medium pt-1 border-t border-slate-200">
                      💡 Please transfer the total payable amount of <strong>₹{totalPreview}</strong> to the account above, and upload the transfer receipt below.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block font-medium">
                    Payment Proof {totalPreview > 0 ? "(Required)" : "(Optional)"}
                  </label>
                  {popupData.is_fee_exempt && Number(popupData.registration_fee || 0) === 0 && (
                    <p className="text-xs text-emerald-700 mb-1">
                      Registration and escort fee exemption is active. Upload proof only if you have payable charges.
                    </p>
                  )}
                  <input type="file" name="payment_proof" accept="image/*,.pdf" onChange={handleChange} className="w-full border p-2 rounded" />
                </div>

                {/* Escort(s) */}
                <h3 className="font-semibold mt-4 text-indigo-900">
                  {guideRunnerRequired ? "Guide Runner Information (required for blind category)" : "Escort Information (optional)"}
                </h3>

                {/* Escort 1 */}
                <div className="p-2 border rounded">
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner ID" : "Escort ID"}</label>
                    <input type="text" name="escort_id_1" value={escorts[0].escort_id} readOnly className="w-full border p-2 rounded bg-gray-100" />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}</label>
                    <input
                      type="text"
                      name="escort_name_1"
                      placeholder={guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}
                      value={escorts[0].escort_name}
                      onChange={(e) => handleEscortChange(0, e)}
                      className="w-full border p-2 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Email" : "Escort Email"}</label>
                    <input type="email" name="escort_email_1" placeholder={guideRunnerRequired ? "Guide Runner Email (optional)" : "Escort Email (optional)"} value={escorts[0].escort_email || ""} onChange={(e) => handleEscortChange(0, e)} className="w-full border p-2 rounded mt-1" />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Type" : "Escort Type"}</label>
                    <input
                      type="text"
                      name="escort_type_1"
                      placeholder={guideRunnerRequired ? "Guide Runner" : "Coach / Parent / Helper"}
                      value={escorts[0].escort_type}
                      onChange={(e) => handleEscortChange(0, e)}
                      className="w-full border p-2 rounded mt-1"
                    />
                  </div>
                  <div>
                    <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Photo" : "Escort Photo"}</label>
                    <input type="file" name="escort_photo_1" accept="image/*" onChange={(e) => handleEscortChange(0, e)} className="w-full border p-2 rounded mt-1" />
                  </div>
                </div>

                {/* Add second escort toggle */}
                {!showSecondEscort && (
                  <div className="text-right">
                    <button type="button" onClick={() => {
                      setShowSecondEscort(true);
                      setEscorts((prev) => {
                        const copy = [...prev];
                        copy[1] = {
                          ...copy[1],
                          escort_id: copy[1].escort_id || getEscortId(2),
                          escort_type: copy[1].escort_type || (guideRunnerRequired ? "Guide Runner" : copy[1].escort_type),
                        };
                        return copy;
                      });
                    }} className="text-sm text-indigo-900 font-semibold underline">
                      {guideRunnerRequired ? "+ Add second guide runner" : "+ Add second escort"}
                    </button>
                  </div>
                )}

                {showSecondEscort && (
                  <div className="p-2 border rounded">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Escort 2</h4>
                      <button type="button" className="text-sm text-red-600 font-semibold" onClick={() => {
                        setShowSecondEscort(false);
                        // clear second escort data
                        setEscorts((prev) => {
                          const copy = [...prev];
                          copy[1] = { escort_id: getEscortId(2), escort_name: "", escort_email: "", escort_type: "", escort_photo: null };
                          return copy;
                        });
                      }}>Remove</button>
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner ID" : "Escort ID"}</label>
                      <input type="text" name="escort_id_2" value={escorts[1].escort_id} readOnly className="w-full border p-2 rounded mt-1 bg-gray-100" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Name" : "Escort Name"}</label>
                      <input type="text" name="escort_name_2" placeholder={guideRunnerRequired ? "Guide Runner 2 Name" : "Escort 2 Name"} value={escorts[1].escort_name} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Email" : "Escort Email"}</label>
                      <input type="email" name="escort_email_2" placeholder={guideRunnerRequired ? "Guide Runner 2 Email (optional)" : "Escort 2 Email (optional)"} value={escorts[1].escort_email || ""} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Type" : "Escort Type"}</label>
                      <input type="text" name="escort_type_2" placeholder={guideRunnerRequired ? "Guide Runner" : "Coach / Parent / Helper"} value={escorts[1].escort_type} onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                    <div>
                      <label className="block font-medium">{guideRunnerRequired ? "Guide Runner Photo" : "Escort Photo"}</label>
                      <input type="file" name="escort_photo_2" accept="image/*" onChange={(e) => handleEscortChange(1, e)} className="w-full border p-2 rounded mt-1" />
                    </div>
                  </div>
                )}

                {/* Total preview */}
                <div className="text-right text-sm font-medium">
                  <div>
                    Registration fee: ₹{popupData.registration_fee || 0}
                    {popupData.is_fee_exempt && Number(popupData.registration_fee || 0) === 0 ? " (exempted)" : ""}
                  </div>
                  <div>
                    Escort fee per escort: ₹{popupData.escort_fee || 0}
                    {popupData.is_fee_exempt && Number(popupData.escort_fee || 0) === 0 ? " (exempted)" : ""}
                  </div>
                  <div>Selected escorts: {selectedEscortCount}</div>
                  <div className="mt-1">Total (preview): <span className="font-semibold">₹{totalPreview}</span></div>
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid || submitting}
                  className={`w-full py-2.5 rounded-lg text-white font-semibold transition-all shadow-sm flex items-center justify-center gap-2 ${
                    isFormValid && !submitting ? "bg-indigo-900 hover:bg-indigo-950" : "bg-slate-350 cursor-not-allowed opacity-50"
                  }`}
                >
                  {submitting ? (
                    <>
                      <FaSpinner className="animate-spin text-sm" />
                      <span>Processing Registration...</span>
                    </>
                  ) : (
                    <span>Submit Registration</span>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
        <DistrictContactsModal
          isOpen={showDistrictModal}
          onClose={() => setShowDistrictModal(false)}
        />
      </div>
  );
};

export default EventRegistration;
